import React, { createContext, useContext, useState } from 'react'
import { Form } from 'react-bootstrap'
import eqxApiInstance, { useApi } from '../../classes/eqxApi'
import { formatDateTime, formatDateTimeInput } from '../../utils/formatDate'
import nl2br from '../../utils/nl2br'
import './EqxForm.css'
import EqxLabelRow from './EqxFormLabelRow'

interface eqxFormStateInterface {
    data?: any
    status?: string
    touched?: string[]
    errors?: any
    serverErrors?: any
    errorMessage?: string | null
    triggerSubmit?: boolean
    params?: any
}
const eqxFormStatuses = {
    IDLE: 'IDLE',
    SUBMITTING: 'SUBMITTING',
    SUBMITTED: 'SUBMITTED',
    SUBMITTED_SERVER: 'SUBMITTED_SERVER',
    COMPLETE: 'COMPLETE',
}
const useFormState = (state?: eqxFormStateManager, initialState?: eqxFormStateInterface) => {
    initialState = initialState ?? {}

    const [data, setData] = React.useState<any>(initialState.data ?? {})
    const [status, setStatus] = React.useState<string>(initialState.status ?? eqxFormStatuses.IDLE)
    const [touched, setTouched] = React.useState<string[]>(initialState.touched ?? [])
    const [errors, setErrors] = React.useState<any>(initialState.errors ?? {})
    const [serverErrors, setServerErrors] = React.useState<any>(initialState.serverErrors ?? {})
    const [errorMessage, setErrorMessage] = React.useState<string | null>(initialState.errorMessage ?? null)
    const [triggerSubmit, setTriggerSubmit] = React.useState<boolean>(initialState.triggerSubmit ?? false)
    const [params, setParams] = React.useState<any>(initialState.params ?? {})

    return (
        state ??
        new eqxFormStateManager({
            data,
            setData,
            status,
            setStatus,
            touched,
            setTouched,
            errors,
            setErrors,
            serverErrors,
            setServerErrors,
            errorMessage,
            setErrorMessage,
            triggerSubmit,
            setTriggerSubmit,
            params,
            setParams,
        })
    )
}
class eqxFormStateManager {
    data: any
    setData: (data: any) => void

    status: string
    setStatus: (data: string) => void

    touched: string[]
    setTouched: (touched: string[]) => void

    errors: any
    setErrors: (errors: any) => void

    serverErrors: any
    setServerErrors: (serverErrors: any) => void

    errorMessage: string | null
    setErrorMessage: (errorMessage: string | null) => void

    triggerSubmit: boolean
    setTriggerSubmit: (triggerSubmit: boolean) => void

    params: any
    setParams: (params: any) => void

    initialData: any
    required: string[]
    validate?: (data: any) => any
    validationRules: { input: string; rule: (val: string) => string | null }[]

    constructor(state: {
        data: any
        setData: (data: any) => void
        status: string
        setStatus: (status: string) => void
        touched: string[]
        setTouched: (touched: string[]) => void
        errors: any
        setErrors: (errors: any) => void
        serverErrors: any
        setServerErrors: (serverErrors: any) => void
        errorMessage: string | null
        setErrorMessage: (errorMessage: string | null) => void
        triggerSubmit: boolean
        setTriggerSubmit: (triggerSubmit: boolean) => void
        params: any
        setParams: (params: any) => void
    }) {
        this.data = state.data
        this.setData = state.setData
        this.status = state.status
        this.setStatus = state.setStatus
        this.touched = state.touched
        this.setTouched = state.setTouched
        this.errors = state.errors
        this.setErrors = state.setErrors
        this.serverErrors = state.serverErrors
        this.setServerErrors = state.setServerErrors
        this.errorMessage = state.errorMessage
        this.setErrorMessage = state.setErrorMessage
        this.triggerSubmit = state.triggerSubmit
        this.setTriggerSubmit = state.setTriggerSubmit
        this.params = state.params
        this.setParams = state.setParams

        this.initialData = state.data
        this.required = []
        this.validationRules = []
    }
    setValue(input: string, value: any) {
        const newData = { ...this.data }
        newData[input] = value
        this.data = newData //so that we can set multiple values before re-rendering
        this.setData(newData)
    }
    getValue(input: string) {
        return this.data[input]
    }
    reset() {
        this.setData({ ...this.initialData })
        this.setTouched([])
        this.setErrors({})
        this.setServerErrors({})
        this.setStatus(eqxFormStatuses.IDLE)
    }
    isLoading() {
        return this.status == eqxFormStatuses.SUBMITTING
    }
    setInputIsRequired(input: string) {
        if (!this.required.includes(input)) this.required.push(input)
    }
    inputShowError(name: string) {
        return this.status != eqxFormStatuses.IDLE || this.touched.includes(name)
    }
    inputHasError(name: string) {
        return this.inputGetError(name) ? true : false
    }
    inputGetError(name: string) {
        return this.errors[name] ?? this.serverErrors[name]
    }
    removeServerError(name: string) {
        let serverErrors = { ...this.serverErrors }
        if (serverErrors && serverErrors[name]) delete serverErrors[name]
        this.serverErrors = serverErrors //so that we can set multiple values before re-rendering
        this.setServerErrors(serverErrors)
    }
    touch(name: string) {
        if (!this.touched.includes(name)) {
            let touched = this.touched
            touched.push(name)
            this.touched = touched
            this.setTouched(touched)
        }
    }
    inputHandleChange(name: string, value: any) {
        this.setValue(name, value)
    }
    inputHandleBlur(name: string, formData: any) {
        this.touch(name)
        this.removeServerError(name)
        if (this.validate) {
            const errors = this.validate(formData)
            this.setErrors(errors)
            this.errors = errors //set temporarily so that others can see errors before re-render
        }
    }
    submit() {
        this.setTriggerSubmit(true)
    }
    setParam(name: string, value: any) {
        const params = { ...this.params }
        params[name] = value
        this.params = params
        this.setParams(params)
    }
    getParam(name: string) {
        return this.params[name]
    }
    addError(name: string, message: string) {
        const errors = { ...this.errors }
        errors[name] = message
        this.errors = errors
        this.setErrors(errors)
    }
    addValidationRule(input: string, rule: (val: string) => string | null) {
        this.validationRules.push({ input, rule })
    }
    isValid() {
        return Object.keys(this.errors).length === 0
    }
}
const useFormContext = (): eqxFormStateManager => {
    const context = useContext(EqxFormContext)
    if (!context) throw new Error('useFormContext must be used within an EqxFormContextProvider')
    return context
}
const useFormContextOptional = (): eqxFormStateManager | null => {
    const context = useContext(EqxFormContext)
    return context
}
interface EqxFormContextProviderProps {
    value: eqxFormStateManager
    children: React.ReactNode
}
const EqxFormContext = createContext<eqxFormStateManager | null>(null)
const EqxFormContextProvider = (props: EqxFormContextProviderProps) => {
    const existingContext = useFormContextOptional()
    if (existingContext) {
        return <>{props.children}</>
    }
    return <EqxFormContext.Provider value={props.value}>{props.children}</EqxFormContext.Provider>
}

interface EqxFormRowProps {
    name: string
    label?: string
    comment?: string
    hasCols?: boolean
    children?: React.ReactNode
    fullWidth?: true
}
const EqxFormRow = (props: EqxFormRowProps) => {
    const form = useFormContext()
    const showError = form.inputShowError(props.name)
    const error = form.inputGetError(props.name)
    const hasCols = props.hasCols ?? false
    return (
        <EqxLabelRow {...props} hasCols={hasCols} error={showError ? error : null}>
            {props.children}
        </EqxLabelRow>
    )
}
interface EqxFormColProps {
    className?: string
    children?: React.ReactNode
}
const EqxFormCol = (props: EqxFormColProps) => {
    let className = props.className ?? 'col'
    className += ' form-col'
    return <div className={className}>{props.children}</div>
}
interface EqxFormInputTextProps {
    name: string
    placeholder?: string
    className?: string
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
    required?: boolean
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    validationRules?: ((val: string) => string | null) | ((val: string) => string | null)[]
    disabled?: boolean
}
const EqxFormInputText = (props: EqxFormInputTextProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }

    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)

    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        form.inputHandleChange(name, e.target.value)
        if (props.onChange) props.onChange(e)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        form.inputHandleBlur(name, data)
        if (props.onBlur) props.onBlur(e)
    }
    const validationRules =
        typeof props.validationRules == 'function' ? [props.validationRules] : props.validationRules ?? []
    React.useEffect(() => {
        for (let i = 0; i < validationRules.length; i++) {
            const rule = validationRules[i]
            form.addValidationRule(name, rule)
        }
    }) //had to remove [] - was clearing out on each edit
    return (
        <input
            type="text"
            value={value ?? ''}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={props.onFocus}
            placeholder={props.placeholder}
            disabled={props.disabled}
            className={
                'form-control ' +
                (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '') +
                (' ' + (props.className ?? ''))
            }
        />
    )
}
interface EqxFormInputNumberProps {
    name: string
    placeholder?: string
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
    required?: boolean
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
    min?: number
    max?: number
    step?: number
    disabled?: boolean
}
const EqxFormInputNumber = (props: EqxFormInputNumberProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }

    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)

    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.value) {
            return true
        }
        const val = parseFloat(e.target.value)
        if (isNaN(val)) {
            form.addError(name, 'Must be a number')
        }
        if (props.min != undefined && val < props.min) {
            form.addError(name, 'Must be greater than ' + props.min)
        }
        if (props.max != undefined && val > props.max) {
            form.addError(name, 'Must be less than ' + props.max)
        }
        form.inputHandleChange(name, val)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        form.inputHandleBlur(name, data)
    }
    React.useEffect(() => {
        form.addValidationRule(name, (val: string) => {
            if (!val) {
                return null
            }
            const valNumber = parseFloat(val)
            if (isNaN(valNumber)) {
                return 'Must be a number'
            }
            return null
        })
        if (props.min) {
            const min = props.min
            form.addValidationRule(name, (val: string) => {
                if (!val) {
                    return null
                }
                const valNumber = parseFloat(val)
                if (isNaN(valNumber)) {
                    return 'Must be a number'
                }
                if (valNumber < min) {
                    return 'Must be greater than ' + min
                }
                return null
            })
        }
        if (props.max) {
            const max = props.max
            form.addValidationRule(name, (val: string) => {
                if (val == '') {
                    return null
                }
                const valNumber = parseFloat(val)
                if (isNaN(valNumber)) {
                    return 'Must be a number'
                }
                if (valNumber > max) {
                    return 'Must be less than ' + max
                }
                return null
            })
        }
    })
    return (
        <input
            min={props.min}
            max={props.max}
            step={props.step}
            type="number"
            value={value ?? ''}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={props.onFocus}
            placeholder={props.placeholder}
            disabled={props.disabled}
            className={'form-control ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
        />
    )
}
interface EqxFormInputCheckboxProps {
    standard?: boolean
    name: string
    required?: boolean
    type?: 'checkbox' | 'switch' | undefined
    onFocus?: (e?: React.FocusEvent<HTMLInputElement>) => void
    onChange?: (e?: React.ChangeEvent<HTMLInputElement>) => void
    label?: string
}
const EqxFormInputCheckbox = (props: EqxFormInputCheckboxProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }
    const type = props.type ?? 'switch'

    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)

    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        if (props.onChange) props.onChange(e)
        onClick(e.target.checked)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        onBlur()
    }
    const onClick = function (active: boolean) {
        form.inputHandleChange(name, active)
    }
    const onBlur = function () {
        form.inputHandleBlur(name, data)
    }
    return (
        <div className="form-checkbox-container">
            <Form.Check
                type={type}
                checked={value ? true : false}
                name={name}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={props.onFocus}
                className={
                    (type != 'switch' ? 'form-check-input' : '') +
                    ' ' +
                    (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')
                }
            />
            {props.label && <label className="small text-muted ms-1">{props.label}</label>}
        </div>
    )
}
interface EqxFormSubmitProps {
    label?: string
    loading?: boolean
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    variant?: 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark'
    size?: 'sm' | 'md' | 'lg'
    className?: string
}
const EqxFormSubmit = (props: EqxFormSubmitProps) => {
    const form = useFormContextOptional()
    const label = props.label ?? 'Submit'
    const loading = (form && form.status == eqxFormStatuses.SUBMITTING) ?? (props.loading ? true : false)
    const onClick = props.onClick ? props.onClick : () => { }
    return (
        <button className={"btn btn-" + (props.variant ?? 'success') + " " + (props.size ? "btn-" + props.size : "") + " " + (props.className ?? "")} disabled={loading} onClick={onClick}>
            {label}
            {loading && (
                <span className="spinner-border spinner-border-sm">
                    <span className="visually-hidden">Loading...</span>
                </span>
            )}
        </button>
    )
}
interface EqxFormInputTextareaProps {
    name: string
    placeholder?: string
    required?: boolean
}
const EqxFormInputTextarea = (props: EqxFormInputTextareaProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }

    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)

    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLTextAreaElement>) {
        form.inputHandleChange(name, e.target.value)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLTextAreaElement>) {
        form.inputHandleBlur(name, data)
    }
    return (
        <textarea
            value={value}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={props.placeholder}
            className={'form-control ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
        />
    )
}
interface EqxFormInputHiddenProps {
    name: string
}
const EqxFormInputHidden = (props: EqxFormInputHiddenProps) => {
    const form = useFormContext()
    const name = props.name

    const data = form.data

    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        form.inputHandleChange(name, e.target.value)
    }
    return <input type="hidden" value={value} name={name} onChange={handleChange} />
}
interface EqxFormInputDateTimeProps {
    name: string
    placeholder?: string
    required?: boolean
}
const EqxFormInputDateTime = (props: EqxFormInputDateTimeProps) => {
    const [showDateInput, setShowDateInput] = useState<boolean>(false)
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }

    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)

    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        form.inputHandleChange(name, e.target.value)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        form.inputHandleBlur(name, data)
        setShowDateInput(false)
    }
    const valueForInput = formatDateTimeInput(value)
    const valueForView = formatDateTime(value)
    return showDateInput ? (
        <input
            type="datetime-local"
            value={valueForInput}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={'form-control ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
        />
    ) : (
        <input
            type="text"
            className={'form-control ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
            onFocus={() => setShowDateInput(true)}
            value={valueForView}
            placeholder={props.placeholder}
            readOnly={true}
        />
    )
}
interface EqxFormInputDateProps {
    name: string
    placeholder?: string
    required?: boolean
}
const EqxFormInputDate = (props: EqxFormInputDateProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }
    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)
    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        form.inputHandleChange(name, e.target.value)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        form.inputHandleBlur(name, data)
    }
    return (
        <input
            type="date"
            value={value ?? ''}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={props.placeholder}
            className={'form-control ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
        />
    )
}

interface EqxFormInputTimeProps {
    name: string
    placeholder?: string
    required?: boolean
}
const EqxFormInputTime = (props: EqxFormInputTimeProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }
    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)
    const value = data[name]
    const handleChange = function (e: React.ChangeEvent<HTMLInputElement>) {
        form.inputHandleChange(name, e.target.value)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        form.inputHandleBlur(name, data)
    }
    return (
        <input
            type="time"
            value={value ?? ''}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={props.placeholder}
            className={'form-control ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
        />
    )
}
interface EqxFormInputSelectProps extends EqxElementSelectProps {
    name: string
    required?: boolean
}
const EqxFormInputSelect = (props: EqxFormInputSelectProps) => {
    const form = useFormContext()
    const name = props.name
    const required = props.required
    if (required) {
        form.setInputIsRequired(name)
    }

    const data = form.data
    const showError = form.inputShowError(name)
    const hasError = form.inputHasError(name)
    const errorText = form.inputGetError(name)

    const value = data[name]
    const onChange = function (e: React.ChangeEvent<HTMLSelectElement>) {
        form.inputHandleChange(name, e.target.value)
        if (props.onChange) props.onChange(e)
    }
    const onBlur = function (e: React.FocusEvent<HTMLSelectElement>) {
        form.inputHandleBlur(name, data)
        if (props.onBlur) props.onBlur(e)
    }
    return (
        <EqxElementSelect
            {...props}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            showError={showError}
            error={showError && hasError ? errorText : undefined}
        />
    )
}
interface EqxElementSelectProps {
    name?: string
    value?: string | number
    onChange?: Function
    onBlur?: Function
    options: { value: string; label: string }[]
    showError?: boolean
    error?: string
    blankText?: string
    placeholder?: string
}
const EqxElementSelect = (props: EqxElementSelectProps) => {
    const handleChange = function (e: React.ChangeEvent<HTMLSelectElement>) {
        if (props.onChange) props.onChange(e)
    }
    const handleBlur = function (e: React.FocusEvent<HTMLSelectElement>) {
        if (props.onBlur) props.onBlur(e)
    }
    const showError = props.showError ?? false
    const hasError = props.error ? true : false
    const blankText = props.blankText ?? 'Please select...'
    return (
        <select
            value={props.value}
            name={props.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={'form-control form-select ' + (showError ? (!hasError ? 'is-valid' : 'is-invalid') : '')}
        >
            <option value="">{blankText}</option>
            {props.options &&
                props.options.map((option, i) => (
                    <option key={`${i}-${option.value}`} value={option.value}>
                        {option.label}
                    </option>
                ))}
        </select>
    )
}
interface EqxFormInputGroupProps {
    before?: React.ReactNode
    after?: React.ReactNode
    children: React.ReactNode
}
const EqxFormInputGroup = (props: EqxFormInputGroupProps) => {
    let before = props.before ?? []
    let after = props.after ?? []
    return (
        <div className="input-group">
            {before}
            {props.children}
            {after}
        </div>
    )
}
const EqxFormInputGroupText = (props: { children: React.ReactNode }) => {
    return <div className="input-group-text">{props.children}</div>
}
const EqxFormInputGroupButton = (props: { children: React.ReactNode; onClick: () => void }) => {
    return (
        <a className="btn btn-primary" onClick={props.onClick}>
            {props.children}
        </a>
    )
}
interface EqxFormProps {
    api: eqxApiInstance
    state?: eqxFormStateManager
    validate?: (data: any) => {}
    prepareData?: (data: any) => any
    action: string
    method?: string
    children?: React.ReactNode
    onSuccess?: (response: any) => void
    onError?: (response: any) => void
    onFailValidation?: (errors: any) => void
    onLoading?: () => void
    showErrorHeader?: boolean
}
const EqxForm = (props: EqxFormProps) => {
    const state = useFormState(props.state)
    const api = useApi()
    const showErrorHeader = props.showErrorHeader ?? true

    React.useEffect(() => {
        if (state.triggerSubmit) {
            handleSubmit()
            state.setTriggerSubmit(false)
        }
    }, [state.triggerSubmit])

    const formData = state.data
    const prepareData = props.prepareData ?? ((data: any) => data)
    const action = props.action
    const method = props.method ?? 'post'
    const onSuccess = props.onSuccess ?? ((response: any) => { })
    const onError = props.onError ?? ((message: string, response: any) => { })

    const validate = (data: any) => {
        const errors: any = {}
        //first validate required
        for (let i = 0; i < state.required.length; i++) {
            const name = state.required[i]
            if (!data[name]) {
                errors[name] = 'Required'
            }
        }
        //validate rules
        for (let i = 0; i < state.validationRules.length; i++) {
            const rule = state.validationRules[i]
            const error = rule.rule(data[rule.input])
            if (error) {
                errors[rule.input] = error
            }
        }
        //then validate function
        const errors2 = props.validate ? props.validate(data) : {}
        const errorsToReturn = { ...errors, ...errors2 }
        return errorsToReturn
    }
    state.validate = validate

    const error = state.errorMessage
    const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
        if (e) e.preventDefault()
        let data = formData
        const errors = validate(data)
        data = prepareData({ ...data })
        state.setErrors(errors)
        if (Object.keys(errors).length > 0) {
            if (props.onFailValidation) props.onFailValidation(errors)
            state.setErrorMessage(
                'One or more errors occurred - please check the information below and submit the form again.'
            )
            state.setStatus(eqxFormStatuses.SUBMITTED)
            return
        }
        if (props.onLoading) props.onLoading()
        state.setErrorMessage(null)
        try {
            state.setStatus(eqxFormStatuses.SUBMITTING)
            //setTimeout(() => {
            const promise = api.request(action, method, data)
            promise
                .then((response) => {
                    state.setStatus(eqxFormStatuses.COMPLETE)
                    onSuccess(response)
                })
                .catch((response) => {
                    let errorMessage = response.message ?? 'Sorry, an error occurred'
                    const errors = response.errors ?? response.errorFields ?? {}
                    //append error fields to error message
                    let errorsArr = []
                    if (errorMessage) errorsArr.push(errorMessage)
                    for (const key in errors) {
                        errorsArr.push(errors[key])
                    }
                    errorMessage = errorsArr.join('\r\n')
                    state.setStatus(eqxFormStatuses.SUBMITTED_SERVER)
                    state.setServerErrors(errors)
                    state.setErrorMessage(errorMessage)
                    onError(errorMessage, response)
                })
            //}, 1500)
        } catch (e) {
            console.error(e)
        }
    }
    return (
        <EqxFormContextProvider value={state}>
            <form onSubmit={handleSubmit}>
                {error && showErrorHeader && <div className="alert alert-danger">{nl2br(error)}</div>}
                {props.children}
            </form>
        </EqxFormContextProvider>
    )
}

interface EqxFormInputRadioButtonsProps {
    name: string;
    options: { value: string | number; label: string }[];
    required?: boolean;
    label?: string;
    inline?: boolean;
    disabled?: boolean;
    className?: string;
    onBlur?: Function;
    onChange?: Function;
}
const EqxFormInputRadioButtons = (props: EqxFormInputRadioButtonsProps) => {
    const form = useFormContext();
    const name = props.name;
    const required = props.required;
    if (required) {
        form.setInputIsRequired(name);
    }
    const data = form.data;
    const showError = form.inputShowError(name);
    const hasError = form.inputHasError(name);
    const value = data[name];
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        form.inputHandleChange(name, e.target.value);
        if (props.onChange) props.onChange(e);
    };
    const handleBlur = function (e: React.FocusEvent<HTMLInputElement>) {
        form.inputHandleBlur(name, data);
        if (props.onBlur) props.onBlur(e);
    }
    // Inline is true by default
    const isInline = props.inline !== false;
    return (
        <div className={'d-flex ' + (isInline ? 'btn-group' : 'btn-group-vertical') + ' ' + (props.className || '')} role="group" aria-label={props.label || name}>
            {props.options.map((opt, idx) => (
                <React.Fragment key={opt.value}>
                    <input
                        type="radio"
                        className="btn-check"
                        name={name}
                        id={`${name}_${opt.value}`}
                        autoComplete="off"
                        value={opt.value}
                        checked={value === opt.value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={props.disabled}
                        required={required && idx === 0}
                    />
                    <label
                        className={
                            'btn btn-outline-primary' +
                            (value === opt.value ? ' active' : '') +
                            (props.disabled ? ' disabled' : '')
                        }
                        htmlFor={`${name}_${opt.value}`}
                    >
                        {opt.label}
                    </label>
                </React.Fragment>
            ))}
        </div>
    );
}

export {
    EqxForm as default,
    EqxElementSelect,
    EqxFormCol,
    EqxFormContextProvider,
    EqxFormInputCheckbox, EqxFormInputDate, EqxFormInputDateTime, // newly added
    EqxFormInputGroup,
    EqxFormInputGroupButton,
    EqxFormInputGroupText,
    EqxFormInputHidden,
    EqxFormInputNumber, EqxFormInputRadioButtons, EqxFormInputSelect,
    EqxFormInputText,
    EqxFormInputTextarea, // newly added
    EqxFormInputTime, EqxFormRow, // renamed
    eqxFormStatuses,
    EqxFormSubmit,
    useFormContext,
    useFormState,
    type EqxFormProps,
    type eqxFormStateInterface,
    type eqxFormStateManager
}

